import React, { useCallback } from 'react';

// local dependencies
import { includes, find, noop, isObject, isEmpty, map, filter, castArray, get, size, set, isUndefined, cloneDeep } from 'lodash';
import classnames from 'classnames';
import { Icon } from 'modules/icon-bank';
import { checkbox as Checkbox } from 'ui/views/generic/checkbox';
import { getLabel } from 'utils/getLabel';
import { SELECT_ALL_OPTION_VALUE, GLOBAL_LABELS } from 'constants/common';
import { dateRangePicker as DateRangePicker } from 'ui/views/generic/dateRangePicker';
import { CagFilter } from 'modules/cagFilter';
import shortid from 'shortid';
import { popover } from 'modules/popover';
import { ChoiceList } from 'modules/choice-list';
import { SingleSelectAndSearch } from 'modules/singleSelectAndSearch';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { reportsFilters as ReportsFilters } from 'ui/views/widgets/reportsFilters';
 *
 * // render
 * <ReportsFilters propName='propValue'/>
 */

 // mood icon names based on option name
const MOOD_ICONS = {
    angry: 'faceAngry',
    sad: 'faceSad',
    neutral: 'faceNeutral',
    happy: 'faceHappy',
};

 // filter option
const FilterOption = ( props ) => {

    // check if option is selected
    const isSelected = includes( props.selected, props.option[ 'value' ] );

    // check if disabled
    const disabled = get( props, 'option.disabled', false );

    // compute class names
    const widgetClassName = classnames( 'ui-reports-filters__filters__filter__options__option', {
        'ui-reports-filters__filters__filter__options__option--active': isSelected,
        'ui-reports-filters__filters__filter__options__option--is-select-all': props.option.value === SELECT_ALL_OPTION_VALUE,
        'ui-reports-filters__filters__filter__options__option--disabled': disabled
    } );

    const iconClassName= classnames( 'ui-reports-filters__filters__filter__options__option__main-option__icon', {
        'ui-reports-filters__filters__filter__options__option__main-option__icon--active': isSelected,
    } );
    
    // get display label
    const _label = props.option.title || props.option.label;
    const label = isObject( _label ) ? getLabel( _label ) : _label;

    
    return (
        <div className={ widgetClassName }>
            
            {/* main option */}
            <div className='ui-reports-filters__filters__filter__options__option__main-option' onClick={ props.onClick } title={ label }>

                {
                    props.showCheckbox || SELECT_ALL_OPTION_VALUE === props.option.name ? (
                        <div className='ui-reports-filters__filters__filter__options__option__main-option__checkbox'>
                            <Checkbox size='xs' value={ isSelected } disabled={ disabled } />
                        </div>
                    ) : null
                }
                {/* checkbox */}
                
                {/* label text */}
                {
                    props.showCheckbox || SELECT_ALL_OPTION_VALUE === props.option.name ? (
                        <div className='ui-reports-filters__filters__filter__options__option__main-option__label' dangerouslySetInnerHTML = { { __html: label } } />
                    ) : (
                        <div className={ iconClassName }>
                            <Icon name={ MOOD_ICONS[ props.option.name ] }/>
                        </div>
                    )
                }
            </div>
        </div>
    );
};

// set display name
FilterOption.displayName = 'FilterOption';

/******************************************** */

/*-------*/

// filter button
const FilterButton = ( props ) => {

    // open popover options
    const openPopover = useCallback( ( element ) => {
        popover.open( {
            referenceElement: element,
            view: ChoiceList,
            tooltip: false,
            isDropdown: true,
            props: {
                width: props.flyoutWidth,
                allowFilter: true,
                displaySelectAllOption: true,
                type: props.type,
                options: props.options,
                placeholder: props.placeholder,
                selected: isEmpty( props.value ) ? [] : castArray( props.value ),
                onChange: props.onChange,
                choiceListClassName: 'ui-reports-filters__header__reasons__choice-list',
            }
        } );
    }, [ props.value ] );

    return (
        <div className='ui-reports-filters__header__reasons' onClick={ ( event ) => openPopover( event.currentTarget ) }>
            <div className='ui-reports-filters__header__reasons__title'>
                { props.filterTitle }
            </div>
            <div className='ui-reports-filters__header__reasons__bottom'>
                <div className='ui-reports-filters__header__reasons__bottom__value'>
                    { props.targetValue }
                </div>
                <div className='ui-reports-filters__header__reasons__bottom__icon'>
                    <Icon name='downArrow' width='14' height='14'/>
                </div>
            </div>
        </div>
    );
};

// set display name
FilterButton.displayName = 'table.FilterButton';

export const reportsFilters = ( props ) => {

    const filterFields = props.filterFields;

    // extract the visible filters
    const callRange = cloneDeep( find( filterFields, { name: 'callRange' }, {} ) );
    const cag = find( filterFields, { name: 'cag' }, {} );
    const categoryAndReasons = find( filterFields, { name: 'categoryAndReasons' }, {} );
    const agents = find( filterFields, { name: 'agents' }, {} );
    const callType = find( filterFields, { name: 'callType' }, [] );
    const callerType = find( filterFields, { name: 'callerType' }, [] );
    const moodType = find( filterFields, { name: 'callerMood' }, [] );
    const resolutionType = find( filterFields, { name: 'callResolution' }, [] );

    const filterOptions = get( props.filterData, 'options' );

    // get the Selected values for call Type
    let selectedCallType = props.filterData.filter.callType;
    
    if( callType && callType.config.allowSelectAll && includes( selectedCallType, SELECT_ALL_OPTION_VALUE ) ) {
        selectedCallType = map( filter( get( filterOptions, `${callType.options}`, [] ), _option => ! isEmpty( _option[ 'value' ] ) ), 'value' ); // select all options when `selectAll` option is selected
    }

    // get the Selected values for caller Type
    let selectedCallerType = props.filterData.filter.callerType;
    
    if( callerType && callerType.config.allowSelectAll && includes( selectedCallerType, SELECT_ALL_OPTION_VALUE ) ) {
        selectedCallerType = map( filter( props.filterData.options.callerTypeOptions, _option => ! isEmpty( _option[ 'value' ] ) ), 'value' ); // select all options when `selectAll` option is selected
    }

    // get the Selected values for mood Type
    let selectedMoodType = props.filterData.filter.callerMood;
    
    if( moodType && moodType.config.allowSelectAll && includes( selectedMoodType, SELECT_ALL_OPTION_VALUE ) ) {
        selectedMoodType = map( filter( props.filterData.options.callerMood, _option => ! isEmpty( _option[ 'value' ] ) ), 'value' ); // select all options when `selectAll` option is selected
    }

    // get the Selected values for caller Type
    let selectedResolutionType = props.filterData.filter.callResolution;
    
    if( resolutionType && resolutionType.config.allowSelectAll && includes( selectedResolutionType, SELECT_ALL_OPTION_VALUE ) ) {
        selectedResolutionType = map( filter( props.filterData.options.resolutionTypeOptions, _option => ! isEmpty( _option[ 'value' ] ) ), 'value' ); // select all options when `selectAll` option is selected
    }

    if( ! isUndefined( callRange ) && ! isEmpty( get( callRange, 'config.frequency', {} ) ) ) {
        const frequencyKey = get( callRange, 'config.frequency.options' );
        const frequencyOptions = get( props, `filterData.options.${frequencyKey}`, [] );
        set( callRange, 'config.frequency.options', frequencyOptions );
    }

    return (
        <div className='ui-reports-filters'>
            <div className='ui-reports-filters__header'>
                <div className='ui-reports-filters__header__title'>
                    { getLabel( props.widgetConfig.title ) }
                </div>
                {
                    callRange ? (
                        <DateRangePicker
                            className='ui-reports-filters__header__date-range'
                            value={ props.filterData.filter.callRange }
                            placeholder={ getLabel( find( filterFields, { name: callRange.name } ).title ) }
                            onChange={ ( value ) => props.onOptionSelect( { options: value, optionType: callRange.name } ) }
                            config={ get( callRange, 'config' ) }
                            frequency={ get( props.filterData, 'filter.frequency', null ) }
                            onFrequencyChange={ ( value ) => props.onOptionSelect( { options: value,  optionType: 'frequency' } )}
                        />
                    ) : null
                }
                {
                    cag ? (
                        <CagFilter
                            className='ui-reports-filters__header__cag'
                            displayValue="ALL"
                            filter={ {
                                title: cag.title,
                                url: cag.url,
                            } }
                            value={ get( props.filterData, 'filter.cag', [] ) }
                            onChange={ props.onCagChange }
                        />
                    ) : null
                }
                {
                    agents ? (

                        <SingleSelectAndSearch
                            { ...agents }
                            className={ 'ui-reports-filters__header__agents' }
                            value = { get( props, 'filterData.filter.agents' ) }
                            onChange={ ( { options, value } ) => {

                                if( 0 === size( options ) ) {
                                    props.onClearFilter( { optionType: 'agents' } );
                                } else {
                                    const option = find( options, { value: value } );
                                    props.onOptionSelect( { option, options, selected: value, optionType: get( agents, 'name' ), isSingleSelect: true } );
                                }
                                
                            } }
                        />

                    ) : null
                }
                {
                    categoryAndReasons ? (
                        <div className= 'ui-reports-filters__header__reason' >
                            <FilterButton
                                type={  categoryAndReasons.type }
                                options={ props.filterData.options.categoryAndReasons }
                                placeholder={ `Search ${ getLabel( categoryAndReasons.title ) }` }
                                flyoutWidth={ get( props.layoutConfig, `filterFlyoutWidths.${ categoryAndReasons }`, 250 ) }
                                filterTitle={ getLabel( categoryAndReasons.title ) }
                                targetValue= { getLabel( GLOBAL_LABELS.DISPLAY_TEXT_ALL ) }
                                value={ get( props, 'filterData.filter.categoryAndReasons' ) }
                                onChange= { ( value ) => {
                                    props.onOptionSelect( { selected: value, optionType: get( categoryAndReasons, 'name' ) } );

                                } }
                            />
                        </div>
                    ) : null
                }
            </div>

            <div className='ui-reports-filters__filters'>
                {
                    callType ? (
                        <div className='ui-reports-filters__filters__filter'>
                            <div className='ui-reports-filters__filters__filter__title' >{ getLabel( callType.title ) }</div>
                            <div className='ui-reports-filters__filters__filter__options'>
                                {
                                    get( filterOptions, `${callType.options}`, [] ).map( ( option ) => {
                                        return (
                                            ! callType.config.allowSelectAll && option.name === SELECT_ALL_OPTION_VALUE ? null : (
                                                <FilterOption
                                                    key={ shortid.generate() }
                                                    option={ option }
                                                    selected={  selectedCallType }
                                                    showCheckbox={ true }
                                                    disabled={ get( option, 'disabled', false ) }
                                                    onClick={ option.disabled ? noop : () => props.onOptionSelect( { option: option, options: get( filterOptions, `${callType.options}` ), selected: selectedCallType, optionType: callType.name, isSelectAllVisible: callType.config.allowSelectAll } ) }
                                                />
                                            )
                                        );
                                    } )
                                }
                            </div>
                        </div>
                    ) : null
                }
                {
                    callerType ? (
                        <div className='ui-reports-filters__filters__filter'>
                            <div className='ui-reports-filters__filters__filter__title'>{ getLabel( callerType.title ) }</div>
                            <div className='ui-reports-filters__filters__filter__options'>
                                {
                                    props.filterData.options.callerTypeOptions.map( ( option ) => {
                                        return (
                                            <FilterOption
                                                key={ shortid.generate() }
                                                option={ option }
                                                selected={  selectedCallerType }
                                                showCheckbox={ true }
                                                onClick={ option.disabled ? noop : () => props.onOptionSelect( { option: option, options: props.filterData.options.callerTypeOptions, selected: selectedCallerType, optionType: callerType.name, isSelectAllVisible: callerType.config.allowSelectAll } ) }
                                            />
                                        );
                                    } )
                                }
                            </div>
                        </div>
                        
                    ) : null
                }
                {
                    moodType ? (
                        <div className='ui-reports-filters__filters__filter'>
                            <div className='ui-reports-filters__filters__filter__title'>{ getLabel( moodType.title ) }</div>
                            <div className='ui-reports-filters__filters__filter__options'>
                                {
                                    props.filterData.options.callerMood.map( ( option ) => {
                                        return (
                                            <FilterOption
                                                key={ shortid.generate() }
                                                option={ option }
                                                selected={  selectedMoodType }
                                                showCheckbox= { false }
                                                onClick={ option.disabled ? noop : () => props.onOptionSelect( { option: option, options: props.filterData.options.callerMood, selected: selectedMoodType, optionType: moodType.name, isSelectAllVisible: moodType.config.allowSelectAll } ) }
                                            />
                                            
                                        );
                                    } )
                                }
                            </div>
                        </div>
                    ) : null
                }
                {
                    resolutionType ? (
                        <div className='ui-reports-filters__filters__filter'>
                            <div className='ui-reports-filters__filters__filter__title'>{ getLabel( resolutionType.title ) }</div>
                            <div className='ui-reports-filters__filters__filter__options'>
                                {
                                    props.filterData.options.resolutionTypeOptions.map( ( option ) => {
                                        return (
                                            <FilterOption
                                                key={ shortid.generate() }
                                                option={ option }
                                                selected={  selectedResolutionType }
                                                showCheckbox={ true }
                                                onClick={ option.disabled ? noop : () => props.onOptionSelect( { option: option, options: props.filterData.options.resolutionTypeOptions, selected: selectedResolutionType, optionType: resolutionType.name, isSelectAllVisible: resolutionType.config.allowSelectAll } ) }
                                            />
                                        );
                                    } )
                                }
                            </div>
                        </div>
                    ) : null
                }
            </div>
        </div>
    );
};

// set display name
reportsFilters.displayName = 'reportsFilters';

// set default props
reportsFilters.defaultProps = {
    filterData: {
        filters: {},
        options: {}
    },
    filterField: [], // visibility true fields
    widgetConfig: {}, // contains the title
    onOptionSelect: noop
};

// prop types
reportsFilters.propTypes = {};
